/**
 * Token Utilities for Security
 * Handles token validation, expiration checking, and security operations
 */

/**
 * Check if a JWT token is expired
 * @param {string} token - JWT token string
 * @returns {boolean} - True if token is expired or invalid
 */
export function isTokenExpired(token) {
  if (!token) return true;
  
  try {
    // JWT tokens have 3 parts: header.payload.signature
    const parts = token.split('.');
    if (parts.length !== 3) return true;
    
    // Decode the payload (second part)
    const payload = JSON.parse(atob(parts[1]));
    
    // Check if token has expiration claim
    if (!payload.exp) return false; // No expiration means it doesn't expire
    
    // Check if token is expired (exp is in seconds, Date.now() is in milliseconds)
    const currentTime = Math.floor(Date.now() / 1000);
    return payload.exp < currentTime;
  } catch (error) {
    console.error('Error checking token expiration:', error);
    return true; // If we can't parse, consider it expired
  }
}

/**
 * Get token expiration time
 * @param {string} token - JWT token string
 * @returns {Date|null} - Expiration date or null if invalid
 */
export function getTokenExpiration(token) {
  if (!token) return null;
  
  try {
    const parts = token.split('.');
    if (parts.length !== 3) return null;
    
    const payload = JSON.parse(atob(parts[1]));
    if (!payload.exp) return null;
    
    return new Date(payload.exp * 1000);
  } catch (error) {
    console.error('Error getting token expiration:', error);
    return null;
  }
}

/**
 * Get time until token expires (in milliseconds)
 * @param {string} token - JWT token string
 * @returns {number|null} - Milliseconds until expiration or null
 */
export function getTimeUntilExpiration(token) {
  if (!token) return null;
  
  try {
    const parts = token.split('.');
    if (parts.length !== 3) return null;
    
    const payload = JSON.parse(atob(parts[1]));
    if (!payload.exp) return null;
    
    const expirationTime = payload.exp * 1000;
    const currentTime = Date.now();
    const timeUntilExpiration = expirationTime - currentTime;
    
    return timeUntilExpiration > 0 ? timeUntilExpiration : 0;
  } catch (error) {
    console.error('Error calculating time until expiration:', error);
    return null;
  }
}

/**
 * Validate token format
 * @param {string} token - JWT token string
 * @returns {boolean} - True if token format is valid
 */
export function isValidTokenFormat(token) {
  if (!token || typeof token !== 'string') return false;
  
  const parts = token.split('.');
  return parts.length === 3;
}

/**
 * Clear all authentication data from localStorage
 */
export function clearAllAuthData() {
  // User tokens
  localStorage.removeItem('authToken');
  localStorage.removeItem('refreshToken');
  localStorage.removeItem('userData');
  
  // Admin tokens
  localStorage.removeItem('adminAuthToken');
  localStorage.removeItem('adminRefreshToken');
  localStorage.removeItem('adminAuth');
  localStorage.removeItem('adminData');
  
  // Clear any session storage
  sessionStorage.clear();
}

/**
 * Check if user should be redirected to login
 * @param {string} token - JWT token string
 * @param {string} userType - 'user' or 'admin'
 * @returns {boolean} - True if should redirect
 */
export function shouldRedirectToLogin(token, userType = 'user') {
  if (!token) return true;
  if (!isValidTokenFormat(token)) return true;
  if (isTokenExpired(token)) return true;
  return false;
}

